/**
 * SOLID Review
 * S — Strong. One use case: an error the Framework caught — ask the Domain whether to send it, send it.
 * O — Strong. The caps are the decision's; the wire is Infrastructure's.
 * L — Strong. Implements its use-case role exactly.
 * I — Strong. `execute`.
 * D — Strong. The decision it asks, the diagnostics server and the session's step, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IPropsClientInput } from '../../domain/ports/schemas/ClientChanges';
import type { ICommandClientError } from '../../domain/ports/schemas/DiagnosticsMoves';
import type { IPortClientDiagnosticsServer } from '../ports/contracts';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import { DONE, type IServiceSerialStep } from '../shared/ClientSteps';

export interface IConfigDepDiagnostics {
  readonly decisions: Pick<IPortClientDecisions, 'planClientError'>;
  readonly diagnostics: IPortClientDiagnosticsServer;
  readonly step: IServiceSerialStep<IPropsClientInput>;
}

/**
 * WHAT WENT WRONG IN THIS BROWSER (client error reporting, plan 2026-10-09, EP5; a use case since frontend-application-oo P4).
 * The decision runs in the serial section like every other; the send happens OUTSIDE it, so a slow network never holds the
 * client. Whatever the send answers is answered, never thrown: the Framework drops it, because a failed report must not
 * become an error the reporter would then report.
 */
export class ReportClientErrorUseCase implements IUseCase<ICommandClientError, TPortOutcome> {
  constructor(private readonly deps: IConfigDepDiagnostics) {}

  async execute(ctx: IContextRequest, command: ICommandClientError): Promise<TPortOutcome> {
    const { decisions, step, diagnostics } = this.deps;
    const plan = await step.run(ctx, (input) => decisions.planClientError(ctx, input, command));
    if (!plan.success) return plan;
    if (plan.value.report === null) return DONE;
    return diagnostics.send(ctx, plan.value.report);
  }
}
